import { useCallback, useState } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { useTranslation } from 'react-i18next';
import { cn } from '@scio3/ui';
import type { HSLA, RGBA } from './color-utils';

type ColorData =
  | { hex: string; source: string }
  | { r: number; source: string }
  | { g: number; source: string }
  | { b: number; source: string }
  | { a: number; source: string }
  | { h: number; s: number; l: number; source: string };

type Props = {
  hex: string;
  rgb: RGBA;
  hsl: HSLA;
  disableAlpha: boolean;
  onChange: (data: ColorData) => void;
};

type ViewMode = 'hex' | 'rgb' | 'hsl';

const inputClassName = cn(
  'h-[21px] w-full rounded-sm border-0 text-center text-[11px] text-foreground',
  'shadow-[inset_0_0_0_1px_hsl(var(--border))]',
  'focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_1px_hsl(var(--ring))]',
);

const labelClassName = cn(
  'mt-1 block text-center text-[11px] uppercase leading-[11px] text-muted-foreground',
);

function EditableField({
  label,
  value,
  arrowOffset = 1,
  onChange,
}: {
  label: string;
  value: string | number;
  arrowOffset?: number;
  onChange: (data: Record<string, string | number>) => void;
}) {
  const [currentValue, setCurrentValue] = useState(String(value).toUpperCase());
  const [isFocused, setIsFocused] = useState(false);

  // Sync external value when not focused
  const displayValue = isFocused ? currentValue : String(value).toUpperCase();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const stringValue = e.currentTarget.value;
      const isPercentage = stringValue.includes('%');
      const num = Number(stringValue.replace(/%/g, ''));
      if (isNaN(num)) return;

      if (e.key === 'ArrowUp') {
        const newVal = Math.round((num + arrowOffset) * 100) / 100;
        onChange({ [label]: isPercentage ? `${newVal}%` : newVal });
        setCurrentValue(isPercentage ? `${newVal}%` : String(newVal));
      } else if (e.key === 'ArrowDown') {
        const newVal = Math.round((num - arrowOffset) * 100) / 100;
        onChange({ [label]: isPercentage ? `${newVal}%` : newVal });
        setCurrentValue(isPercentage ? `${newVal}%` : String(newVal));
      }
    },
    [arrowOffset, label, onChange],
  );

  // Update local state + propagate every typed change. A previous implementation
  // relied on `onKeyUp` alone, but a controlled `<input value={...} />` without
  // `onChange` has React reset the DOM value on every render — so the keystroke
  // was swallowed before `onKeyUp` could observe it. `onChange` is the correct
  // seam for typed input; `onKeyDown` still handles ArrowUp / ArrowDown stepping.
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setCurrentValue(val);
      onChange({ [label]: val });
    },
    [label, onChange],
  );

  return (
    <div className="w-full">
      <input
        className={inputClassName}
        spellCheck={false}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          setCurrentValue(String(value).toUpperCase());
        }}
        aria-label={label}
      />
      <span className={labelClassName}>{label}</span>
    </div>
  );
}

export function ColorInput({ hex, rgb, hsl, disableAlpha, onChange }: Props) {
  const { t } = useTranslation();
  const [view, setView] = useState<ViewMode>(() => (hsl.a === 1 ? 'hex' : 'rgb'));

  const toggleViews = useCallback(() => {
    if (view === 'hex') setView('rgb');
    else if (view === 'rgb') setView('hsl');
    else setView('hex');
  }, [view]);

  const handleChange = useCallback(
    (data: Record<string, string | number>) => {
      if ('hex' in data) {
        const tc = new TinyColor(String(data.hex));
        if (tc.isValid) {
          onChange({
            hex: disableAlpha ? tc.toHex() : tc.toHex8(),
            source: 'hex',
          });
        }
      } else if ('r' in data || 'g' in data || 'b' in data) {
        onChange({
          r: Number(data.r ?? rgb.r),
          g: Number(data.g ?? rgb.g),
          b: Number(data.b ?? rgb.b),
          source: 'rgb',
        } as { r: number; source: string });
      } else if ('a' in data) {
        let a = Number(data.a);
        a = Math.max(0, Math.min(1, a));
        if (disableAlpha) a = 1;
        onChange({
          h: hsl.h,
          s: hsl.s,
          l: hsl.l,
          a: Math.round(a * 100) / 100,
          source: 'rgb',
        } as { a: number; source: string });
      } else if ('h' in data || 's' in data || 'l' in data) {
        const s = data.s && String(data.s).replace('%', '');
        const l = data.l && String(data.l).replace('%', '');
        onChange({
          h: Number(data.h ?? hsl.h),
          s: Number(s || hsl.s),
          l: Number(l || hsl.l),
          source: 'hsl',
        });
      }
    },
    [disableAlpha, rgb, hsl, onChange],
  );

  return (
    <div className="flex pt-1.5">
      <div className="flex flex-1 gap-1.5">
        {view === 'hex' && <EditableField label="hex" value={hex} onChange={handleChange} />}
        {view === 'rgb' && (
          <>
            <EditableField label="r" value={rgb.r} onChange={handleChange} />
            <EditableField label="g" value={rgb.g} onChange={handleChange} />
            <EditableField label="b" value={rgb.b} onChange={handleChange} />
            {!disableAlpha && (
              <EditableField label="a" value={rgb.a} arrowOffset={0.01} onChange={handleChange} />
            )}
          </>
        )}
        {view === 'hsl' && (
          <>
            <EditableField label="h" value={Math.round(hsl.h)} onChange={handleChange} />
            <EditableField
              label="s"
              value={`${Math.round(hsl.s * 100)}%`}
              onChange={handleChange}
            />
            <EditableField
              label="l"
              value={`${Math.round(hsl.l * 100)}%`}
              onChange={handleChange}
            />
            {!disableAlpha && (
              <EditableField label="a" value={hsl.a} arrowOffset={0.01} onChange={handleChange} />
            )}
          </>
        )}
      </div>

      {/* Toggle button */}
      <div className="w-8 text-right">
        <button
          type="button"
          className="relative mr-[-4px] mt-1 cursor-pointer"
          onClick={toggleViews}
          aria-label={t('Toggle color mode')}
        >
          <svg className="h-6 w-6" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M12,5.83L15.17,9L16.58,7.59L12,3L7.41,7.59L8.83,9L12,5.83Z"
            />
            <path
              fill="currentColor"
              d="M12,18.17L8.83,15L7.42,16.41L12,21L16.59,16.41L15.17,15Z"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
